import React, { useEffect, useMemo, useRef, useState } from "react";
import "./style.scss";
import Logo from "../../assets/images/payroll_preview/guhroo-payroll-logo.svg";
import moment from "moment";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { Spinner } from "react-bootstrap";

// 🔹 Helper to format currency
const formatCurrency = (val) => (val ? `$${Number(val).toFixed(2)}` : "$0.00");

// 🔹 Transform API data → payrollData
const mapEmployeeCompensationsToPayrollData = (employeeCompensations = []) => {
  const parseAmount = (val) =>
    parseFloat((val || 0).toString().replace(/[^0-9.-]+/g, "")) || 0;

  return employeeCompensations.map((emp) => {
    // Employee Info
    const employee = {
      name: emp?.emp_details?.emp?.full_name || "N/A",
      address: emp?.emp_details?.home_address?.address || "N/A",
    };

    // Earnings
    const earnings = [];
    (emp.hourly_compensations || []).forEach((comp) => {
      earnings.push({
        description: comp.name,
        hours: comp.hours,
        total: formatCurrency(comp.amount),
      });
    });

    // Paid Time Off
    if (emp.paid_time_off?.length > 0) {
      const totalPtoHours = emp.paid_time_off.reduce(
        (sum, item) => sum + parseFloat(item.hours || 0),
        0
      );
      const totalPtoAmount = emp.paid_time_off.reduce(
        (sum, item) => sum + parseFloat(item.amount || 0),
        0
      );
      earnings.push({
        description: "Paid Time Off",
        hours: totalPtoHours.toFixed(2),
        total: formatCurrency(totalPtoAmount),
      });
    }

    // Gross
    earnings.push({
      description: "Gross",
      hours: "-",
      total: formatCurrency(emp.gross_pay),
    });

    // Deductions + Benefits
    const deductions = [];
    const combined = [...(emp.deductions || []), ...(emp.benefits || [])];
    combined.forEach((ded) => {
      deductions.push({
        description: ded.name,
        empDeduction: formatCurrency(ded.employee_deduction || ded.amount || 0),
        empContribution: formatCurrency(ded.company_contribution || 0),
      });
    });

    // Taxes
    const employeeTaxes = [];
    const employerTaxes = [];
    (emp.taxes || []).forEach((tax) => {
      const row = { description: tax.name, amount: formatCurrency(tax.amount) };
      if (tax.employer) {
        employerTaxes.push(row);
      } else {
        employeeTaxes.push(row);
      }
    });

    // Totals under Taxes
    if (employeeTaxes.length > 0) {
      const total = employeeTaxes.reduce(
        (sum, t) => sum + parseAmount(t.amount),
        0
      );
      employeeTaxes.push({
        description: "Total",
        amount: formatCurrency(total),
      });
    }
    if (employerTaxes.length > 0) {
      const total = employerTaxes.reduce(
        (sum, t) => sum + parseAmount(t.amount),
        0
      );
      employerTaxes.push({
        description: "Total",
        amount: formatCurrency(total),
      });
    }

    // Totals
    const totals = [
      { description: "Net Pay", amount: formatCurrency(emp.net_pay) },
      { description: "Check Amount", amount: formatCurrency(emp.check_amount) },
      {
        description: "Gross Pay",
        amount: formatCurrency(emp.gross_pay || emp.employer_cost),
      },
    ];

    return {
      employee,
      earnings,
      deductions,
      employeeTaxes,
      employerTaxes,
      totals,
    };
  });
};

const PayrollPreview = ({ employeeData }) => {
  const payrollRef = useRef(null); // 🔹 reference for PDF export
  const [btnLoading, setBtnLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const employeeCompensations = employeeData?.employee_compensations || [];
  const payrollData = useMemo(
    () => mapEmployeeCompensationsToPayrollData(employeeCompensations),
    [employeeCompensations]
  );

  const handleGeneratePDF = async () => {
    const input = payrollRef.current;
    if (!input) return;

    setBtnLoading(true);

    try {
      const canvas = await html2canvas(input, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4", true, 300);
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = - (imgHeight - heightLeft);;
        pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      // 🔹 Instead of saving, create Blob URL
      const pdfBlob = pdf.output("blob");
      const pdfUrl = URL.createObjectURL(pdfBlob);
      setPdfUrl(pdfUrl);
    } catch (error) {
      console.error("PDF generation failed:", error);
    } finally {
      setBtnLoading(false);
    }
  };
  useEffect(() => {
    if (employeeData) {
      handleGeneratePDF();
    }
  }, [employeeData]);

  return (
    <div className="payroll-main-wrapper">
      {/* 🔹 Download Button */}
      {/* <div className="payroll-download-btn-wrapper">
        <BackButtonComponent reDirectionpath="/payroll" />
        <button
          className="download-btn"
          onClick={btnLoading ? null : handleDownloadPDF}
        >
          {btnLoading ? (
            <Spinner animation="border" size="sm" />
          ) : (
            "Download PDF"
          )}
        </button>
      </div> */}
      <div className="">
        {btnLoading ? (
          <div className="d-flex justify-content-center align-items-center loader-overlay">
            <Spinner animation="border" />
          </div>
        ) : pdfUrl ? (
          <iframe
            src={`${pdfUrl}#navpanes=0`}
            title="Payroll PDF Preview"
            style={{ width: "100%", height: "100vh", border: "none" }}
          />
        ) : (
          <p>No data to preview</p>
        )}
      </div>
      {btnLoading ? null : (
        <>
          {!pdfUrl && (
            <div
              ref={payrollRef}
              style={{
                position: pdfUrl === null ? "absolute" : "relative",
                left: pdfUrl === null ? "-999999px" : "0",
              }}
            >
              <div className="payroll-preview-header">
                {/* <div className="payroll-preview-logo">
                  <h1>Report generated by</h1>
                  <img src={Logo} alt="logo" />
                </div> */}
                <div className="payroll-company-info">
                  <h5>{employeeData?.client_details?.legal_name}</h5>
                  <p>
                    {employeeData?.client_details?.address}
                    <br /> {employeeData?.client_details?.city},{" "}
                    {employeeData?.client_details?.state},{" "}
                    {employeeData?.client_details?.zip}
                    <br /> USA
                  </p>
                </div>
              </div>
              <div className="payroll-preview-title">
                <h1>Payroll Journal Report</h1>
                <h2>Payroll Journal Summary by Employee</h2>
                <h3>
                  Per Employee Summary for{" "}
                  {moment(employeeData?.pay_period?.start_date).format(
                    "MM/DD/YYYY"
                  )}
                  -
                  {moment(employeeData?.pay_period?.end_date).format(
                    "MM/DD/YYYY"
                  )}
                </h3>
              </div>
              <div className="payroll-preview-report">
                {" "}
                <table className="payroll-preview-table">
                  <thead>
                    <tr>
                      <th colSpan="1">Employee Information</th>
                      <th colSpan="3">Earnings</th>
                      <th colSpan="3">Deductions/Contributions</th>
                      <th colSpan="2">Employee Taxes</th>
                      <th colSpan="2">Employer Taxes</th>
                      <th colSpan="2">Totals</th>
                    </tr>
                    <tr>
                      <th></th>
                      <th>Description</th>
                      <th>Hours</th>
                      <th>Total</th>
                      <th>Description</th>
                      <th>Employee Deduction</th>
                      <th>Employer Contribution</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Description</th>
                      <th>Amount</th>
                      <th>Description</th>
                      <th>Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payrollData.map((emp, idx) => {
                      const maxRows = Math.max(
                        emp.earnings.length,
                        emp.deductions.length,
                        emp.employeeTaxes.length,
                        emp.employerTaxes.length,
                        emp.totals.length
                      );

                      return Array.from({ length: maxRows }).map(
                        (_, rowIdx) => {
                          const isLastRow = rowIdx === maxRows - 1;
                          const getCellStyle = (hasBorderLeft, isTotalRow) => ({
                            borderTop: "none",
                            borderRight: "0.5px solid #000",
                            // borderLeft: hasBorderLeft ? "0.5px solid #000" : "none",
                            borderBottom: isLastRow
                              ? "0.5px solid #000"
                              : "none",
                            background: isTotalRow ? "#EAEAEA" : undefined,
                          });
                          const isTotalLike = (desc, allowGross = false) =>
                            desc === "Total" ||
                            (allowGross && desc === "Gross");

                          const safe = (val) =>
                            val !== undefined && val !== "" ? val : "-";

                          return (
                            <tr key={`${idx}-${rowIdx}`}>
                              {rowIdx === 0 && (
                                <td
                                  rowSpan={maxRows}
                                  className="employee-info"
                                  style={{
                                    borderBottom: "0.5px solid #000000",
                                    verticalAlign: "top",
                                  }}
                                >
                                  <strong>{safe(emp.employee.name)}</strong>
                                  <p>{safe(emp.employee.address)}</p>
                                </td>
                              )}
                              {/* Earnings */}
                              <td
                                style={getCellStyle(
                                  true,
                                  isTotalLike(emp.earnings[rowIdx]?.description)
                                )}
                              >
                                {safe(emp.earnings[rowIdx]?.description)}
                              </td>
                              <td
                                style={getCellStyle(
                                  true,
                                  isTotalLike(emp.earnings[rowIdx]?.description)
                                )}
                              >
                                {safe(emp.earnings[rowIdx]?.hours)}
                              </td>
                              <td
                                style={getCellStyle(
                                  true,
                                  isTotalLike(emp.earnings[rowIdx]?.description)
                                )}
                              >
                                {safe(emp.earnings[rowIdx]?.total)}
                              </td>
                              {/* Deductions */}
                              <td
                                style={getCellStyle(
                                  false,
                                  isTotalLike(
                                    emp.deductions[rowIdx]?.description
                                  )
                                )}
                              >
                                {safe(emp.deductions[rowIdx]?.description)}
                              </td>
                              <td
                                style={getCellStyle(
                                  false,
                                  isTotalLike(
                                    emp.deductions[rowIdx]?.description
                                  )
                                )}
                              >
                                {safe(emp.deductions[rowIdx]?.empDeduction)}
                              </td>
                              <td
                                style={getCellStyle(
                                  false,
                                  isTotalLike(
                                    emp.deductions[rowIdx]?.description
                                  )
                                )}
                              >
                                {safe(emp.deductions[rowIdx]?.empContribution)}
                              </td>
                              {/* Employee Taxes */}
                              <td
                                style={getCellStyle(
                                  false,
                                  isTotalLike(
                                    emp.employeeTaxes[rowIdx]?.description
                                  )
                                )}
                              >
                                {safe(emp.employeeTaxes[rowIdx]?.description)}
                              </td>
                              <td
                                style={getCellStyle(
                                  false,
                                  isTotalLike(
                                    emp.employeeTaxes[rowIdx]?.description
                                  )
                                )}
                              >
                                {safe(emp.employeeTaxes[rowIdx]?.amount)}
                              </td>
                              {/* Employer Taxes */}
                              <td
                                style={getCellStyle(
                                  false,
                                  isTotalLike(
                                    emp.employerTaxes[rowIdx]?.description
                                  )
                                )}
                              >
                                {safe(emp.employerTaxes[rowIdx]?.description)}
                              </td>
                              <td
                                style={getCellStyle(
                                  false,
                                  isTotalLike(
                                    emp.employerTaxes[rowIdx]?.description
                                  )
                                )}
                              >
                                {safe(emp.employerTaxes[rowIdx]?.amount)}
                              </td>
                              {/* Totals */}
                              <td
                                style={getCellStyle(
                                  false,
                                  isTotalLike(emp.totals[rowIdx]?.description)
                                )}
                              >
                                {safe(emp.totals[rowIdx]?.description)}
                              </td>
                              <td
                                style={getCellStyle(
                                  false,
                                  isTotalLike(emp.totals[rowIdx]?.description)
                                )}
                              >
                                {safe(emp.totals[rowIdx]?.amount)}
                              </td>
                            </tr>
                          );
                        }
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default PayrollPreview;
